import moment from 'moment';
import Autolinker from 'autolinker';
import { BuyerType, MessageSource } from '@/constants';
import { Buyer, Member, MessageData, MessageEntity, MessageThread, Recipients, ThreadMember, User } from '@/types';
import { calculateDateTimeDifference } from '@/util/date-helper';

const generateUniqueId = (length = 16) => {
  return parseInt(
    Math.ceil(Math.random() * Date.now())
      .toPrecision(length)
      .toString()
      .replace('.', '')
  );
};

const convertThreadMemberToBuyer = (member: ThreadMember) => ({
  firstName: member.firstName,
  lastName: member.lastName,
  email: member.email,
  buyerType: member.role,
  phone: '',
  image: member.photoUrl,
  sortNumber: ''
});
const convertThreadMemberToTeamMember = (member: ThreadMember) => ({
  area: [] as string[],
  firstName: member.firstName,
  lastName: member.lastName,
  userId: +member.id,
  title: member.role,
  email: member.email,
  image: member.photoUrl,
  status: member.status,
  isDefault: false,
  teamGroup: '',
  salesforceId: ''
});

const generateMessageData = (data: any, messageId: string, messageThreadId: string): MessageData => ({
  messageId,
  messageThreadId,
  sender: data.sender,
  weekNumber: data.weekNumber,
  messageData: data.messageData,
  messageSubject: data.messageSubject,
  attachments: data.attachments,
  createdDate: data.createdDate,
  updatedDate: data.updatedDate,
  source: MessageSource.PORTAL

});

const generateConversationRecord = (data: any, messageId: string, messageThreadId: string): MessageThread => ({
  jobId: data.jobId,
  messageThreadId,
  groupName: messageThreadId,
  members: data.members,
  lastMessageData: {
    messageId,
    sender: data.sender,
    messageData: data.messageData,
    messageSubject: data.messageSubject,
    createdDate: data.createdDate,
    updatedDate: data.updatedDate
  },
  createdDate: data.createdDate,
  lastModifiedOn: data.updatedDate,
  createdBy: data.createdBy,
  updatedBy: data.updatedBy,
  isNewMsg: true
});

const isNewMessage = (date: string) => {
  const config = process.env.VUE_APP_NEW_MESSAGE_CONFIG;
  const minDate = calculateDateTimeDifference(new Date(), +config);
  return (new Date(date) > minDate);
};

const setNewMessageFlag = (data: MessageThread[]) => {
  const conversations = [] as MessageThread[];
  data.forEach(entry => {
    const el = entry;
    el.isNewMsg = isNewMessage(entry.lastMessageData.createdDate);
    conversations.push(el);
  });
  return conversations;
};

const generateBuyersOfThread = (buyer: User): ThreadMember => ({
  id: buyer.contactId || '',
  name: `${buyer.firstName} ${buyer.lastName}`,
  type: BuyerType.BUYER,
  role: buyer.buyerType,
  email: buyer.email,
  photoUrl: buyer.image || '',
  firstName: buyer.firstName,
  lastName: buyer.lastName,
  status: 'Active'
});

const generateTeamMembersOfThread = (member: Member): ThreadMember => ({
  id: member.userId.toString(),
  name: `${member.firstName} ${member.lastName}`,
  type: 'ADMIN',
  role: member.title,
  email: member.email,
  photoUrl: member.image,
  firstName: member.firstName,
  lastName: member.lastName,
  status: member.status,
  teamGroup: member.teamGroup
});

const generateThreadMembers = (loggedInUser: Buyer, otherBuyers: User[], teamMembers: Member[]): Recipients => {
  const members = [] as ThreadMember[];
  const sender = generateBuyersOfThread(loggedInUser);
  members.push(sender);
  otherBuyers.forEach((buyer: User) => members.push(generateBuyersOfThread(buyer)));
  teamMembers.forEach((member: Member) => members.push(generateTeamMembersOfThread(member)));
  return {
    members,
    sender
  };
};

const generateMessageRequest = (jobId: string, messageThreadId: string, groupName: string, messageData: MessageEntity, recipients: Recipients, customerCareEmail: string) => {
  const requestData = {
    jobId,
    messageThreadId,
    groupName: groupName,
    members: recipients.members,
    messageId: '',
    messageData: messageData.messageData,
    messageSubject: messageData.messageSubject || '',
    attachments: messageData.attachments,
    sender: recipients.sender,
    createdBy: recipients.sender,
    updatedBy: recipients.sender,
    weekNumber: moment(new Date()).week().toString(),
    createdDate: new Date().toISOString(),
    updatedDate: new Date().toISOString(),
    customerCareEmail
  };
  const conversation = generateConversationRecord(requestData, '', messageThreadId);
  const message = generateMessageData(requestData, '', messageThreadId);
  return {
    requestData,
    conversation,
    message
  }
};

const generatelinkedText = (text: string) => {
  const options = {
    email: true,
    phone: true
  }
  const linkedText = Autolinker.link(text, options);
  return linkedText;
}

export {
  convertThreadMemberToBuyer,
  convertThreadMemberToTeamMember,
  setNewMessageFlag,
  isNewMessage,
  generateUniqueId,
  generateThreadMembers,
  generateMessageRequest,
  generatelinkedText
};
